import {
  useHttpClient,
  useNextSortOrder,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import {
  AppointmentProposalOptionProduct,
  AppointmentProposalOptionProductCreate,
  AppointmentProposalOptionProductUpdate,
} from './appointmentProposalOptionProducts.models';
import {
  appointmentProposalOptionProductsReducer,
  AppointmentProposalOptionProductsState,
  initState,
} from './appointmentProposalOptionProducts.reducer';

type CreatePayload = AppointmentProposalOptionProductCreate;
type UpdatePayload = AppointmentProposalOptionProductUpdate;

interface OfflineAppointmentProposalOptionProductsContextProps {
  appendProducts: (products: AppointmentProposalOptionProduct[]) => void;
  upsertProducts: (products: AppointmentProposalOptionProduct[]) => void;
  updateProduct: (
    appointmentProposalOptionId: string,
    appointmentProposalOptionProductId: string,
    update: UpdatePayload
  ) => void;
  selectProposalProducts: (appointmentProposalId: string) => void;
  setProductsMutating: (opts: { creatingIds?: string[] }) => void;
  clearProductsMutating: (opts: {
    creatingIds?: string[];
    error?: string;
  }) => void;
}

interface AppointmentProposalOptionProductsContextProps
  extends OfflineAppointmentProposalOptionProductsContextProps {
  proposalProducts: AppointmentProposalOptionProduct[];
  isLoading: boolean;
  isLoaded: boolean;
  isLoadingProductIds: string[];
  error: string | null;
  fetchAppointmentProposalProducts: (
    appointmentId: string,
    appointmentProposalId: string
  ) => Promise<AppointmentProposalOptionProduct[]>;
  fetchAppointmentProposalOptionProducts: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalOptionId: string
  ) => Promise<AppointmentProposalOptionProduct[]>;
  createAppointmentProposalOptionProduct: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalOptionId: string,
    create: CreatePayload
  ) => Promise<AppointmentProposalOptionProduct>;
  createManyAppointmentProposalOptionProducts: (
    appointmentId: string,
    appointmentProposalId: string,
    payloads: {
      appointmentProposalOptionId: string;
      product: CreatePayload;
    }[]
  ) => Promise<AppointmentProposalOptionProduct[]>;
  updateAppointmentProposalOptionProduct: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalOptionId: string,
    appointmentProposalOptionProductId: string,
    update: UpdatePayload
  ) => Promise<AppointmentProposalOptionProduct>;
  deleteAppointmentProposalOptionProduct: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalOptionId: string,
    appointmentProposalOptionProductId: string
  ) => Promise<void>;
  getNextSortOrder: (offset?: number) => number;
}

export const AppointmentProposalOptionProductsContext = createContext<
  AppointmentProposalOptionProductsContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<AppointmentProposalOptionProductsState>;
}>;

export const AppointmentProposalOptionProductsProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(
    appointmentProposalOptionProductsReducer,
    { ...initState, ...initialState }
  );

  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    state.error && showToast(state.error, 'error');
  }, [state.error, showToast]);

  const proposalProducts = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .filter(
          (a) =>
            a.appointmentProposalId === state.selectedProposalId &&
            a.status !== 'DELETED'
        )
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [state.entities, state.ids, state.selectedProposalId]
  );

  const fetchAppointmentProposalProducts: AppointmentProposalOptionProductsContextProps['fetchAppointmentProposalProducts'] =
    useCallback(
      async (appointmentId, appointmentProposalId) => {
        dispatch({
          type: 'FETCH_APPOINTMENT_PROPOSAL_PRODUCTS',
          payload: { appointmentProposalId },
        });
        try {
          const appointmentProposalOptionProducts = await httpClient.get<
            AppointmentProposalOptionProduct[]
          >(
            `/appointments/${appointmentId}/proposals/${appointmentProposalId}/products`
          );
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSAL_PRODUCTS_SUCCESS',
            payload: {
              appointmentProposalOptionProducts,
              appointmentProposalId,
            },
          });
          return appointmentProposalOptionProducts;
        } catch (error) {
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSAL_PRODUCTS_FAILURE',
            payload: { error: `${error}` },
          });
          return [];
        }
      },
      [httpClient]
    );

  const fetchAppointmentProposalOptionProducts: AppointmentProposalOptionProductsContextProps['fetchAppointmentProposalOptionProducts'] =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId
      ) => {
        dispatch({
          type: 'FETCH_APPOINTMENT_PROPOSAL_OPTION_PRODUCTS',
          payload: { appointmentProposalId },
        });
        try {
          const appointmentProposalOptionProducts = await httpClient.get<
            AppointmentProposalOptionProduct[]
          >(
            `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options/${appointmentProposalOptionId}/products`
          );
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSAL_OPTION_PRODUCTS_SUCCESS',
            payload: {
              appointmentProposalOptionProducts,
              appointmentProposalId,
            },
          });
          return appointmentProposalOptionProducts;
        } catch (error) {
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSAL_OPTION_PRODUCTS_FAILURE',
            payload: { error: `${error}` },
          });
          return [];
        }
      },
      [httpClient]
    );

  const createAppointmentProposalOptionProductInternal = useCallback(
    async (
      appointmentId: string,
      appointmentProposalId: string,
      appointmentProposalOptionId: string,
      create: CreatePayload
    ) => {
      const appointmentProposalOptionProduct =
        await httpClient.post<AppointmentProposalOptionProduct>(
          `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options/${appointmentProposalOptionId}/products`,
          {
            ...create,
            status: 'ACTIVE',
            entityType: 'APPOINTMENT_PROPOSAL_OPTION_PRODUCT',
            externalIds: [],
            images: create.images.map((img) => ({
              type: img.type,
              assetKey: img.assetKey,
            })),
          }
        );
      return appointmentProposalOptionProduct;
    },
    [httpClient]
  );

  const createAppointmentProposalOptionProduct: AppointmentProposalOptionProductsContextProps['createAppointmentProposalOptionProduct'] =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        create
      ) => {
        dispatch({
          type: 'CREATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT',
          payload: { pricebookId: create.pricebookId },
        });
        try {
          const appointmentProposalOptionProduct =
            await createAppointmentProposalOptionProductInternal(
              appointmentId,
              appointmentProposalId,
              appointmentProposalOptionId,
              create
            );
          dispatch({
            type: 'CREATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_SUCCESS',
            payload: { appointmentProposalOptionProduct },
          });
          return appointmentProposalOptionProduct;
        } catch (error) {
          dispatch({
            type: 'CREATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_FAILURE',
            payload: { error: `${error}`, pricebookId: create.pricebookId },
          });
          throw error;
        }
      },
      [createAppointmentProposalOptionProductInternal]
    );

  const createManyAppointmentProposalOptionProducts: AppointmentProposalOptionProductsContextProps['createManyAppointmentProposalOptionProducts'] =
    useCallback(
      async (appointmentId, appointmentProposalId, payloads) => {
        const pricebookIds = payloads.map((p) => p.product.pricebookId);

        dispatch({
          type: 'CREATE_MANY_APPOINTMENT_PROPOSAL_OPTION_PRODUCT',
          payload: { pricebookIds },
        });

        try {
          const createdProducts = await Promise.all(
            payloads.map(({ appointmentProposalOptionId, product }) =>
              createAppointmentProposalOptionProductInternal(
                appointmentId,
                appointmentProposalId,
                appointmentProposalOptionId,
                product
              )
            )
          );

          dispatch({
            type: 'CREATE_MANY_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_SUCCESS',
            payload: {
              pricebookIds,
              appointmentProposalOptionProducts: createdProducts,
            },
          });
          return createdProducts;
        } catch (error) {
          dispatch({
            type: 'CREATE_MANY_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_FAILURE',
            payload: { error: `${error}`, pricebookIds },
          });
          return [];
        }
      },
      [createAppointmentProposalOptionProductInternal]
    );

  const updateAppointmentProposalOptionProduct: AppointmentProposalOptionProductsContextProps['updateAppointmentProposalOptionProduct'] =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        appointmentProposalOptionProductId,
        update
      ) => {
        dispatch({
          type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT',
          payload: {
            appointmentProposalOptionProductId,
            appointmentProposalOptionId,
            update,
          },
        });

        try {
          const appointmentProposalOptionProduct =
            await httpClient.put<AppointmentProposalOptionProduct>(
              `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options/${appointmentProposalOptionId}/products/${appointmentProposalOptionProductId}`,
              update
            );
          dispatch({
            type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_SUCCESS',
            payload: { appointmentProposalOptionProduct },
          });
          return appointmentProposalOptionProduct;
        } catch (error) {
          dispatch({
            type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_FAILURE',
            payload: {
              error: `${error}`,
              appointmentProposalOptionProductId,
              appointmentProposalOptionId,
            },
          });
          throw error;
        }
      },
      [httpClient]
    );

  const deleteAppointmentProposalOptionProduct: AppointmentProposalOptionProductsContextProps['deleteAppointmentProposalOptionProduct'] =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        appointmentProposalOptionProductId
      ) => {
        dispatch({ type: 'DELETE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT' });

        try {
          await httpClient.delete(
            `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options/${appointmentProposalOptionId}/products/${appointmentProposalOptionProductId}`
          );
          dispatch({
            type: 'DELETE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_SUCCESS',
            payload: {
              appointmentProposalOptionProductId,
              appointmentProposalOptionId,
            },
          });
        } catch (error) {
          dispatch({
            type: 'DELETE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_FAILURE',
            payload: { error: `${error}` },
          });
        }
      },
      [httpClient]
    );

  const getNextSortOrder: AppointmentProposalOptionProductsContextProps['getNextSortOrder'] =
    useNextSortOrder(proposalProducts);

  const appendProducts: AppointmentProposalOptionProductsContextProps['appendProducts'] =
    useCallback((appointmentProposalOptionProducts) => {
      dispatch({
        type: 'UPSERT_PRODUCTS',
        payload: { appointmentProposalOptionProducts },
      });
    }, []);

  const updateProduct: AppointmentProposalOptionProductsContextProps['updateProduct'] =
    useCallback(
      (
        appointmentProposalOptionId,
        appointmentProposalOptionProductId,
        update
      ) => {
        dispatch({
          type: 'UPDATE_PRODUCT',
          payload: {
            appointmentProposalOptionProduct: {
              appointmentProposalOptionId,
              appointmentProposalOptionProductId,
              ...update,
            },
          },
        });
      },
      []
    );

  const selectProposalProducts: AppointmentProposalOptionProductsContextProps['selectProposalProducts'] =
    useCallback((appointmentProposalId: string) => {
      dispatch({
        type: 'SELECT_PROPOSAL_PRODUCTS',
        payload: { appointmentProposalId },
      });
    }, []);

  const setProductsMutating: AppointmentProposalOptionProductsContextProps['setProductsMutating'] =
    useCallback((opts) => {
      dispatch({ type: 'SET_PRODUCTS_MUTATING', payload: opts });
    }, []);

  const clearProductsMutating: AppointmentProposalOptionProductsContextProps['clearProductsMutating'] =
    useCallback((opts) => {
      dispatch({ type: 'CLEAR_PRODUCTS_MUTATING', payload: opts });
    }, []);

  return (
    <AppointmentProposalOptionProductsContext.Provider
      value={{
        proposalProducts,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        isLoadingProductIds: state.isLoadingProductIds,
        error: state.error,
        fetchAppointmentProposalProducts,
        fetchAppointmentProposalOptionProducts,
        createAppointmentProposalOptionProduct,
        createManyAppointmentProposalOptionProducts,
        updateAppointmentProposalOptionProduct,
        deleteAppointmentProposalOptionProduct,
        getNextSortOrder,
        appendProducts,
        upsertProducts: appendProducts,
        updateProduct,
        selectProposalProducts,
        setProductsMutating,
        clearProductsMutating,
      }}
    >
      {children}
    </AppointmentProposalOptionProductsContext.Provider>
  );
};

export const useAppointmentProposalOptionProducts = () => {
  const context = useContext(AppointmentProposalOptionProductsContext);
  if (context === undefined) {
    throw new Error(
      'useAppointmentProposalOptionProducts must be used within a AppointmentProposalOptionProductsProvider'
    );
  }
  return context;
};
